/*
 * GALINUR — Marquee bar
 * Continuous, seamless, infinite horizontal scroll (always leftward), RTL-safe.
 *
 * The transform is driven frame-by-frame by requestAnimationFrame — there is no
 * CSS animation with an iteration count that could be dropped or cancelled, so
 * it cannot "stop after one cycle". The offset wraps by exactly one group width
 * (`% groupWidth`), and the track holds ≥2 identical groups, so the wrap is
 * visually seamless with no gap.
 *
 * The CSS carries a plain-keyframe fallback (no custom properties) that runs if
 * this script never executes. Once the script takes over it disables that
 * fallback and owns the transform.
 *
 * Pause on hover / keyboard focus and via the explicit control; resumes and
 * continues indefinitely as soon as the pointer / focus leaves. Honours
 * prefers-reduced-motion (static, centred). Pauses the loop (not the position)
 * while the tab is hidden, purely to save CPU.
 */
if (!customElements.get('marquee-bar')) {
  class MarqueeBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.marquee-bar__track');
      this.group = this.querySelector('.marquee-bar__group');
      this.toggle = this.querySelector('.marquee-bar__toggle');
      if (!this.track || !this.group) return;

      this.speed = Math.max(10, parseInt(this.dataset.speed, 10) || 40); // px per second
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('marquee-bar--static');
        if (this.toggle) this.toggle.hidden = true;
        return;
      }

      this.userPaused = false;
      this.hoverPaused = false;
      this.offset = 0;
      this.groupWidth = 0;
      this.lastTs = 0;
      this._raf = null;
      this._tick = this._tick.bind(this);

      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this._reflectToggle();
        });
      }

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => { this.hoverPaused = true; });
        this.addEventListener('mouseleave', () => { this.hoverPaused = false; });
      }
      this.addEventListener('focusin', () => { this.hoverPaused = true; });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.hoverPaused = false;
      });

      // Take ownership of the transform, then run forever.
      this.track.style.animation = 'none';
      this._measure();
      this._start();

      // Re-measure as fonts / late layout settle (offset stays continuous).
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this._measure());
      }
      window.addEventListener('load', () => this._measure(), { once: true });

      this._ro = new ResizeObserver(() => this._measure());
      this._ro.observe(this);
      this._ro.observe(this.group);

      this._onVisibility = () => {
        if (document.hidden) {
          this._stop();
        } else {
          this.lastTs = 0; // ignore the long hidden gap, don't jump
          this._start();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    disconnectedCallback() {
      this._stop();
      if (this._ro) this._ro.disconnect();
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    _start() {
      if (this._raf == null) this._raf = requestAnimationFrame(this._tick);
    }

    _stop() {
      if (this._raf != null) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _measure() {
      const w = this.group.getBoundingClientRect().width;
      if (w > 8) {
        this.groupWidth = w;
        this._ensureClones();
        if (this.offset >= this.groupWidth) this.offset = this.offset % this.groupWidth;
      }
    }

    _ensureClones() {
      const viewport = this.offsetWidth || this.groupWidth;
      // enough groups that the viewport is always covered after wrapping one group
      const needed = Math.min(40, Math.max(2, Math.ceil((viewport + this.groupWidth) / this.groupWidth) + 1));
      let have = this.track.querySelectorAll('.marquee-bar__group').length;
      while (have < needed) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
        have += 1;
      }
    }

    _tick(ts) {
      this._raf = requestAnimationFrame(this._tick);

      if (!this.lastTs) { this.lastTs = ts; return; }
      const dt = Math.min(0.05, (ts - this.lastTs) / 1000); // clamp big frame gaps
      this.lastTs = ts;

      if (this.userPaused || this.hoverPaused || !this.groupWidth) return;

      this.offset += this.speed * dt;
      if (this.offset >= this.groupWidth) this.offset -= this.groupWidth;
      this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    }

    _reflectToggle() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.dataset.state = this.userPaused ? 'paused' : 'playing';
    }
  }

  customElements.define('marquee-bar', MarqueeBar);
}
